import React from "react";
import dayjs from "dayjs";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { createCheckoutHealthSO, CustomerInterface, i18n, saveCustomer, setSoIdWeb, useAppDispatch, useForm } from "src/common";
import { ArrowRightIcon, Flex, InputBirthDate, InputPhoneNumber, InputText, Button } from "src/components";
import { colors } from "src/theme";
import { AppRoutes } from "src/pages/app";
import { FieldConfig, Form, FormConfig } from "./form";
import { Validation } from "./validation";

export type CustomerFormItemType = 'birthDate' | 'email' | 'phoneNumber';
export const allCustomerFormItems: Array<CustomerFormItemType> = ['birthDate', 'email', 'phoneNumber'];

interface CustomerFormProps {
  customer?: CustomerInterface;
  formItems?: Array<CustomerFormItemType>;
}

// Primjer korištenja komponente forme
export const CustomerForm: React.FC<CustomerFormProps> = ({ customer, formItems = allCustomerFormItems }) => {
  const { formRef, submitForm } = useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [createCheckoutHealthSOMutation] = useMutation(createCheckoutHealthSO);

  const handleSubmit = (values: { [key: string]: any }) => {
    const data: CustomerInterface = {
      ...values,
      age: dayjs().diff(dayjs(values.birthDate), 'year')
    }
    createCheckoutHealthSOMutation({
      variables: {
        birthDate: data.birthDate,
        email: data.email,
        phoneNumber: data.phoneNumber,
        productTypeCode: 'dzo',
        source: 'newDZOfunnel',
        consents: {
          kompareHelp: true,
          phone: true,
          email: true,
          newsletter: true,
          newsletterSpecialOffers: true
        }
      }
    })
      .then((response) => {
        const soId = response.data.createCheckoutHealthSO;
        dispatch(saveCustomer(data));
        dispatch(setSoIdWeb(soId));
        navigate(AppRoutes.OFFERS.fullPath);
      })
      .catch((error) => {
        console.error('Greška prilikom izvršavanja mutacije:', error);
      });
  };

  const fields: FieldConfig[] = [
    {
      name: 'birthDate',
      label: i18n.t('components.forms.customerForm.dateOfBirth'),
      component: InputBirthDate,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validDate', message: 'Unesi ispravan datum rođenja' },
      ] as Validation[],
    },
    {
      name: 'email',
      label: i18n.t('components.forms.customerForm.mail'),
      component: InputText,
      componentProps: {
        type: 'email',
        maxLength: 50
      },
      validations: [
        { type: 'required', message: 'Polje je obavezno.' },
        { type: 'validMail', message: 'Unesite ispravnu e-mail adresu.' },
      ] as Validation[],
    },
    {
      name: 'phoneNumber',
      label: i18n.t('components.forms.customerForm.phone'),
      component: InputPhoneNumber,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validPhoneNumber', message: 'Unesi ispravan broj mobitela', params: { regionCode: 'countryCode' } },
      ] as Validation[],
    }
  ].filter((field) => formItems.includes(field.name as CustomerFormItemType));

  const formConfig: FormConfig = {
    gap: 24
  };

  return <Flex vertical gap={24}>
    <Form ref={formRef} fields={fields} config={formConfig} onSubmit={handleSubmit} initialValues={customer} />
    <Button btnType="primary" iconPosition="right" text={i18n.t('components.forms.customerForm.submit')} style={{ width: '100%' }} onClick={() => submitForm()}>
      <ArrowRightIcon color={colors.white} />
    </Button>
  </Flex>
};
